// src/lib/portal/cartStore.ts
//
// Shopping-cart state for the professional portal.
// Module-level singleton store (useSyncExternalStore): every component
// shares ONE cart instance — a per-hook useState made clear()/addItem()
// invisible to other components and let stale instances overwrite
// localStorage after a submit (found in M2-03 browser verification).
//
// In production this would persist to Postgres via the FastAPI layer
// so the cart survives logout and is preserved across devices.

import { useSyncExternalStore } from "react";

export interface CartItem {
  sku: string;
  name: string;
  brand: string;
  image: string;
  listPriceEUR: number;
  qty: number;
}

const STORAGE_KEY = "avc-cart-v1";

function readStorage(): CartItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartItem[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((it) => typeof it?.sku === "string" && typeof it?.qty === "number");
  } catch {
    return [];
  }
}

function writeStorage(items: CartItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage may be disabled (private mode etc.) — non-fatal
  }
}

let items: CartItem[] = readStorage();
const listeners = new Set<() => void>();

function setItems(next: CartItem[]) {
  items = next;
  writeStorage(items);
  for (const l of listeners) l();
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

function addItem(item: Omit<CartItem, "qty">, qty: number = 1) {
  const existing = items.find((it) => it.sku === item.sku);
  if (existing) {
    setItems(items.map((it) => (it.sku === item.sku ? { ...it, qty: it.qty + qty } : it)));
  } else {
    setItems([...items, { ...item, qty: Math.max(1, qty) }]);
  }
}

function removeItem(sku: string) {
  setItems(items.filter((it) => it.sku !== sku));
}

function setQty(sku: string, qty: number) {
  if (qty <= 0) {
    removeItem(sku);
    return;
  }
  setItems(items.map((it) => (it.sku === sku ? { ...it, qty } : it)));
}

function clear() {
  setItems([]);
}

/**
 * Hook that returns the current shared cart, an add/remove/update helper,
 * and an `itemCount`. State is persisted to localStorage automatically.
 */
export function useCart() {
  const current = useSyncExternalStore(subscribe, () => items);
  return {
    items: current,
    addItem,
    removeItem,
    setQty,
    clear,
    itemCount: current.reduce((s, it) => s + it.qty, 0),
  };
}
